import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import { useAuthStore } from '@/stores/authStore';
import { UNIBOX_ORIGIN } from '@/config';

/**
 * UniBox — the merchant inbox, embedded the same way Analytics is.
 *
 * The frame gets NO token in its URL. UniBox announces `unibox:ready` on
 * mount (and again whenever a token it holds is rejected), and we answer
 * with `unibox:boot` carrying the merchant's ordinary Core bearer. UniBox
 * keeps it in memory only — a bare URL opens nothing, a reload just asks
 * again. See the Phase-5B integration brief (2026-09-02).
 *
 * 🔴 `postMessage`'s second argument must be the real UniBox origin, never
 * '*': the boot message carries a live merchant credential, and '*' would
 * hand it to whatever document happens to occupy the frame.
 */
const Unibox = () => {
  const { i18n } = useTranslation();
  const token = useAuthStore((state) => state.token);
  const frame = useRef<HTMLIFrameElement>(null);

  const lang = i18n.language === 'ar' ? 'ar' : 'en';

  // `?lang=` makes RTL paint on the first frame, before any message arrives;
  // the lang inside unibox:boot handles switches afterwards. Send both.
  const iframeSrc = useMemo(() => `${UNIBOX_ORIGIN}/?lang=${lang}`, [lang]);

  useEffect(() => {
    const post = () => {
      if (!token || !frame.current?.contentWindow) return;
      frame.current.contentWindow.postMessage({ type: 'unibox:boot', token, lang }, UNIBOX_ORIGIN);
    };

    // Registered BEFORE the token guard on purpose: if auth is still
    // resolving on first render, an early return would skip registration,
    // UniBox would announce into the void, and the merchant would sit on the
    // "no access" card until they hit Retry. The effect re-runs when the
    // token lands and posts immediately.
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== UNIBOX_ORIGIN) return;
      if (e.data?.type === 'unibox:ready') post();
      // A customer message arrived. Chrome refuses notification permission
      // inside a cross-origin iframe, so UniBox plays its chime and hands us
      // the banner. Payload carries no token — just a conversation title the
      // merchant is already entitled to see. Degrades to sound-only.
      if (e.data?.type === 'unibox:notify' && 'Notification' in window) {
        if (Notification.permission === 'granted') {
          new Notification(e.data.title, {
            body: e.data.body,
            tag: e.data.conversation_id,
          });
        }
      }
    };

    window.addEventListener('message', onMessage);
    post(); // covers a token that arrives after the frame already announced
    return () => window.removeEventListener('message', onMessage);
  }, [token, lang]);

  if (!token) {
    return (
      <div className='w-full h-[calc(100vh-120px)] flex items-center justify-center'>
        <Spin size='large' />
      </div>
    );
  }

  return (
    // Same container as Analytics — a rounded card inside the page padding.
    <iframe
      ref={frame}
      src={iframeSrc}
      title='UniBox'
      className='w-full h-[calc(100vh-120px)] border-0 rounded-lg overflow-hidden'
      allow='clipboard-write; autoplay'
    />
  );
};

export default Unibox;
